import { useState } from "react";
import useExploreFavoritesStats from "../../../components/hooks/useExploreFavoritesStats";
import useExploreCategoriesQuery from "../../ExploreCategories/hooks/useExploreCategoriesQuery";
import DSCard from "views/system-design/DSCard";
import DSCardContent from "views/system-design/DSCardContent";
import DSBox from "views/system-design/DSBox";
import DSStack from "views/system-design/DSStack";
import DSTypography from "views/system-design/DSTypography";
import DSSelect from "views/system-design/DSSelect";
import DSMenuItem from "views/system-design/DSMenuItem";
import DSSecondaryButton from "views/system-design/DSSecondaryButton";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import {
  DSTable,
  DSTableBody,
  DSTableCell,
  DSTableHead,
  DSTableRow,
} from "views/system-design/Table";

function ExploreFavoritesCard() {
  const [days, setDays] = useState<number>(30);
  const [enabled, setEnabled] = useState(false);
  const { data, isLoading, refetch } = useExploreFavoritesStats(days, enabled);
  const { data: categories } = useExploreCategoriesQuery();

  const categoryNameMap = Object.fromEntries(
    (categories ?? []).map((c) => [c.id, c.name]),
  );

  const handleLoad = () => {
    if (enabled) {
      refetch();
    } else {
      setEnabled(true);
    }
  };

  return (
    <DSCard sx={{ height: "100%" }}>
      <DSBox
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          pt: 2,
          gap: 1,
        }}
      >
        <DSTypography variant="h6">Explore Favorites by Category</DSTypography>
        <DSStack direction="row" alignItems="center" spacing={1}>
          <DSTypography variant="caption" sx={{ color: "text.secondary" }}>
            Last
          </DSTypography>
          <DSSelect<number>
            size="small"
            value={days}
            onChange={(e) => {
              setDays(Number(e.target.value));
              setEnabled(false);
            }}
            sx={{ fontSize: 12 }}
          >
            {[7, 14, 30, 60, 90].map((d) => (
              <DSMenuItem key={d} value={d}>
                {d}d
              </DSMenuItem>
            ))}
          </DSSelect>
          <DSSecondaryButton
            size="small"
            onClick={handleLoad}
            disabled={isLoading}
          >
            {isLoading ? (
              <DSCircularProgress size={20} />
            ) : data ? (
              "Refresh"
            ) : (
              "Load"
            )}
          </DSSecondaryButton>
        </DSStack>
      </DSBox>
      <DSCardContent sx={{ p: 1.5 }}>
        {isLoading ? (
          <DSBox sx={{ textAlign: "center", p: "20px" }}>
            <DSCircularProgress />
          </DSBox>
        ) : data && data.length > 0 ? (
          <DSTable size="small" sx={{ fontSize: 13 }}>
            <DSTableHead>
              <DSTableRow>
                <DSTableCell>Category</DSTableCell>
                <DSTableCell align="center">Favorited</DSTableCell>
                <DSTableCell align="center">Unfavorited</DSTableCell>
              </DSTableRow>
            </DSTableHead>
            <DSTableBody>
              {data.map((row) => (
                <DSTableRow key={row.subModeId} hover>
                  <DSTableCell>
                    {categoryNameMap[row.subModeId] ?? row.subModeId}
                  </DSTableCell>
                  <DSTableCell align="center">
                    {row.favorited.toLocaleString()}
                  </DSTableCell>
                  <DSTableCell align="center">
                    {row.unfavorited.toLocaleString()}
                  </DSTableCell>
                </DSTableRow>
              ))}
            </DSTableBody>
          </DSTable>
        ) : data && data.length === 0 ? (
          <DSBox
            sx={{ textAlign: "center", p: "20px", color: "text.secondary" }}
          >
            No data for this period
          </DSBox>
        ) : (
          <DSBox
            sx={{ textAlign: "center", p: "20px", color: "text.secondary" }}
          >
            Click Load to fetch data
          </DSBox>
        )}
      </DSCardContent>
    </DSCard>
  );
}

export default ExploreFavoritesCard;
